import React, { useState, useEffect, useRef } from "react";
import {
  handleRemoveRegistration,
  handleEditTeamName,
  updatePlayerConfirmation,
} from "services/registrationService";
import { Pencil, Check, Info, Trash2 } from "lucide-react";

const TournamentParticipantsList = ({
  tournament,
  user,
  registrations,
  userDetails,
  locationRankings = [],
  isAdmin = false,
}) => {
  const [editingTeamName, setEditingTeamName] = useState(null);
  const [newTeamName, setNewTeamName] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getUserRanking = (userId) => {
    const rankingEntry = locationRankings.find((r) => r.userId === userId);
    return rankingEntry ? rankingEntry.ranking : null;
  };

  const submitTeamNameEdit = async (regId) => {
    if (!newTeamName.trim()) return;
    await handleEditTeamName(regId, newTeamName);
    setEditingTeamName(null);
    setNewTeamName("");
  };

  const handleCycleConfirmation = async (reg) => {
    const current = reg.confirmed;
    // falsy → 1 (blue) → 2 (green) → false
    // treat legacy `true` same as 1
    const next = !current ? 1 : (current === 1 || current === true) ? 2 : false;
    try {
      await updatePlayerConfirmation(reg.id, next);
    } catch (err) {
      console.error("Failed to update confirmation:", err);
    }
  };

  const handleRemoveWithConfirmation = (registrationId) => {
    const confirmed = window.confirm(
      "Are you sure you want to remove this registration?"
    );
    if (confirmed) {
      handleRemoveRegistration(registrationId);
    }
  };

  return (
    <details className="mt-2 w-full bg-slate-800 rounded-md">
      <summary className="px-2 py-1.5 text-sm font-semibold text-slate-50 cursor-pointer hover:bg-slate-700 rounded-md transition">
        Voir les participants
        <span className="ml-2 text-violet-400">
          ({(registrations || []).length})
        </span>
      </summary>

      <ul className="text-slate-400">
        {registrations?.map((reg, index) => {
          const userInfo = userDetails[reg.userId];
          const ranking = getUserRanking(reg.userId);
          const isWaitingListStart = index === tournament.waitingListLimit;

          return (
            <li key={reg.id}>
              {isWaitingListStart && (
                <div className="p-1 my-1 text-sm font-semibold text-yellow-400 border-b border-l border-yellow-500/50 bg-yellow-500/10 rounded-md">
                  Liste d'attente:
                </div>
              )}
              <div
                className="relative flex justify-between items-center px-2 py-1 bg-slate-800 mb-[1px] rounded-sm"
                ref={dropdownRef}
              >
                <div className="flex flex-row items-center">
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => handleCycleConfirmation(reg)}
                      title="Confirmer le joueur"
                      className={`mr-2 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                        reg.confirmed === 2
                          ? "bg-green-500 border-green-500"
                          : reg.confirmed === 1 || reg.confirmed === true
                          ? "bg-blue-500 border-blue-500"
                          : "bg-transparent border-slate-500"
                      }`}
                    >
                      {reg.confirmed ? (
                        <Check size={12} className="text-white" />
                      ) : null}
                    </button>
                  )}
                  {editingTeamName === reg.id ? (
                    <>
                      <input
                        type="text"
                        value={newTeamName}
                        onChange={(e) => setNewTeamName(e.target.value)}
                        className="w-full px-3 bg-slate-800 border-slate-700 text-slate-50 rounded-md focus:outline-none text-sm focus:ring-2 focus:ring-blue-500"
                        onBlur={() => submitTeamNameEdit(reg.id)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && submitTeamNameEdit(reg.id)
                        }
                      />
                      {isAdmin && (
                        <button
                          className="text-red-400 hover:text-red-300 ml-2"
                          onClick={() => handleRemoveWithConfirmation(reg.id)}
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </>
                  ) : (
                    <span
                      className={`block truncate whitespace-nowrap overflow-hidden text-xs md:text-sm xl:text-base ${
                        isAdmin
                          ? "max-w-[200px] lg:max-w-[400px]"
                          : "max-w-[250px] lg:max-w-[400px]"
                      }`}
                      title={reg.teamName || "Équipe inconnue"}
                    >
                      {`${index + 1}. ${reg.teamName || "Équipe inconnue"}`}
                    </span>
                  )}
                </div>

                <div className="flex items-center space-x-1">
                  {isAdmin &&
                    (editingTeamName === reg.id ? (
                      <button
                        className="text-emerald-400 hover:text-emerald-300 p-1"
                        onClick={() => submitTeamNameEdit(reg.id)}
                        title="Confirmer"
                      >
                        <Check size={14} />
                      </button>
                    ) : (
                      <button
                        className="text-slate-400 hover:text-slate-200 p-1"
                        onClick={() => {
                          setEditingTeamName(reg.id);
                          setNewTeamName(reg.teamName || reg.name);
                        }}
                        title="Modifier"
                      >
                        <Pencil size={14} />
                      </button>
                    ))}

                  {isAdmin && userInfo && (
                    <button
                      className="text-slate-400 hover:text-slate-200 p-1"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setOpenDropdown((prev) =>
                          prev === reg.id ? null : reg.id
                        );
                      }}
                      title="Info"
                    >
                      <Info size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Inline info box below each registration */}
              {openDropdown === reg.id && (
                <div className="my-1 p-4 bg-red-500/10 text-slate-50 text-sm md:text-base rounded-b-md">
                  {reg.teamMateId && (
                    <p className="font-semibold text-slate-50">{reg.teamName}</p>
                  )}
                  <p className="font-semibold text-slate-50">
                    {userInfo.name || "N/A"}
                  </p>
                  <p>
                    <strong>Email:</strong> {userInfo.email || "N/A"}
                  </p>
                  <p>
                    <strong>Téléphone:</strong> {userInfo.phone || "N/A"}
                  </p>
                  {ranking !== null && (
                    <span className="text-sm text-amber-400 ml-2">
                      (Elo: {ranking})
                    </span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {showContactPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-slate-800 p-6 rounded-lg shadow-xl text-center">
            <p className="text-lg font-semibold text-slate-50 mb-4">
              Les inscriptions sont fermées. Veuillez contacter l'organisateur
              pour retirer votre inscription.
            </p>
            <button
              onClick={() => setShowContactPopup(false)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </details>
  );
};

export default TournamentParticipantsList;
